import { useEffect } from 'react';
import { getAppData } from '../actions';
import disambiguateDescriptors from '../utils/disambiguateDescriptors';

/**
 * Custom hook to initialize app data by loading countries, measures, and other essential data
 * @param {Object} state - The app state
 * @param {Function} dispatch - The dispatch function for state updates
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeExternalMetrics - Whether to load external metrics (default: false)
 * @param {boolean} options.includeIndexedYml - Whether to create indexed yml structure (default: false)
 * @param {string} options.errorContext - Context string for error messages (default: 'app')
 * @param {Function} options.onDataLoaded - Callback called after data is loaded with the data object
 * @param {Object} options.dispatchOptions - Additional options to pass to dispatch calls (e.g., onSuccess messages)
 */
export default function useInitializeAppData(state, dispatch, options = {}) {
  const { includeExternalMetrics = false, includeIndexedYml = false, errorContext = 'app', onDataLoaded = null, dispatchOptions = {} } = options;

  useEffect(() => {
    if (!state.fullListOfMeasures || !state.fullListOfCountries) {
      const loadData = async () => {
        try {
          const data = await getAppData();
          const { countries, descriptors, groups, measures, strings, externalMetrics } = data;

          dispatch({
            type: 'descriptors',
            payload: disambiguateDescriptors(descriptors, measures),
            ...dispatchOptions.descriptors,
          });

          dispatch({
            type: 'fullListOfCountryGroups',
            payload: groups,
            ...dispatchOptions.fullListOfCountryGroups,
          });

          dispatch({
            type: 'fullListOfMeasures',
            payload: measures,
            ...dispatchOptions.fullListOfMeasures,
          });

          dispatch({
            type: 'fullListOfCountries',
            payload: countries,
            ...dispatchOptions.fullListOfCountries,
          });

          if (includeExternalMetrics && externalMetrics) {
            dispatch({
              type: 'externalMetrics',
              payload: externalMetrics,
            });
          }

          const translatedText = strings.reduce((acc, string) => {
            return { ...acc, [string.uid]: string };
          }, {});

          dispatch({
            type: 'translatedText',
            payload: translatedText,
          });

          // Initialize the indexedYml data structure if requested
          if (includeIndexedYml) {
            const indexedYml = {};
            measures.forEach((measure) => {
              indexedYml[measure.id] = measure;
            });

            dispatch({
              type: 'indexedYml',
              payload: indexedYml,
            });
          }

          // Call the callback if provided
          if (onDataLoaded) {
            onDataLoaded(data);
          }
        } catch (err) {
          console.error(`Error loading data for ${errorContext}:`, err);
        }
      };

      loadData();
    }
  }, [state.fullListOfMeasures, state.fullListOfCountries]);
}
